import { useState } from 'react'
import { LocalizedLink as Link } from '~/components/LocalizedLink/LocalizedLink'
import Image from '~/components/common/Image'
import { FormattedMessage, useIntl } from 'react-intl'
import clsx from 'classnames'

import type { CatalogPreset } from '~/presets/catalogPresets'
import {
  getCatalogItemPrice,
  getCatalogImageUrl,
  getColorHex,
} from '~/presets/catalogPresets'
import type { ColorName } from '~/utils/colorDictionary'
import { getColorItemByName } from '~/utils/colorDictionary'
import { useCatalogPrice } from '~/hooks/useCatalogPrice'
import { buildCatalogPresetHref } from './buildCatalogPresetHref'
import styles from './CatalogProductCard.module.css'

const TYPE_NAME_MAP: Record<string, string> = {
  wardrobe: 'homepage.products.wardrobe',
  'wardrobe-new-collection': 'homepage.products.new-wardrobe',
  stand: 'homepage.products.stand',
  'tv-stand': 'homepage.products.TVstand',
  bedside: 'homepage.products.bedside',
  rack: 'homepage.products.rack',
  table: 'homepage.products.table',
  'work-table': 'homepage.products.work-table',
}

interface Props {
  preset: CatalogPreset
  priority?: boolean
}

export const CatalogProductCard: React.FC<Props> = ({ preset, priority = false }) => {
  const intl = useIntl()
  const [selectedColor, setSelectedColor] = useState<ColorName>(
    preset.colors[0]
  )

  const href = buildCatalogPresetHref(preset, selectedColor)
  // Live price from /api/pricing/quote (per colour); static fallback for types
  // without a pricing endpoint (table).
  const { price: quotePrice, isStatic } = useCatalogPrice(preset, selectedColor)
  const price = isStatic ? getCatalogItemPrice(preset) : quotePrice
  const imageUrl = getCatalogImageUrl(
    preset.type,
    preset.id,
    selectedColor,
    preset.imageId
  )
  const { width, height, depth } = preset.dimensions
  const formattedPrice =
    price != null
      ? new Intl.NumberFormat('ro-MD', {
          maximumFractionDigits: 0,
        }).format(price)
      : null

  const typeKey =
    preset.type === 'wardrobe' && preset.collection === 'new-collection'
      ? 'wardrobe-new-collection'
      : preset.type
  const typeNameId = TYPE_NAME_MAP[typeKey] ?? preset.type
  const typeName = intl.formatMessage({ id: typeNameId })
  const title =
    preset.type === 'table'
      ? `${typeName} · ${intl.formatMessage({ id: 'homepage.configurator.table.material.chipboard' })}`
      : preset.type === 'work-table' && preset.drawers
        ? `${typeName} ${width}cm cu sertare`
        : `${typeName} ${width}cm`

  return (
    <Link href={href} className={styles.card}>
      <div className={styles.imageWrap}>
        <Image
          src={imageUrl}
          alt={title}
          fill
          sizes="(max-width: 600px) 50vw, 280px"
          className={styles.image}
          unoptimized
          priority={priority}
        />
        {preset.collection === 'new-collection' && (
          <span className={styles.collectionBadge}>
            <FormattedMessage
              id="catalog.badge.newCollection"
              defaultMessage="Colecție nouă"
            />
          </span>
        )}
      </div>

      <div className={styles.swatchesRow}>
        {preset.colors.map((color) => {
          const colorItem = getColorItemByName(color)
          const textureUrl = colorItem?.textureUrl
          const swatchStyle = textureUrl
            ? { backgroundImage: `url(${textureUrl})` }
            : { backgroundColor: getColorHex(color) }

          return (
            <button
              key={color}
              className={clsx(
                styles.swatch,
                textureUrl && styles.swatchTexture,
                color === selectedColor && styles.swatchActive
              )}
              style={swatchStyle}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setSelectedColor(color)
              }}
              aria-label={color}
            />
          )
        })}
      </div>

      <div className={styles.cardBody}>
        <span className={styles.title}>{title}</span>

        <div className={styles.dimensions}>
          {width}&times;{height}&times;{depth}{' '}
          <FormattedMessage
            id="homepage.configurator.dimensions.cm"
            defaultMessage="cm"
          />
        </div>

        <div className={styles.price}>
          {formattedPrice != null ? (
            <FormattedMessage
              id="catalog.price.from"
              defaultMessage="de la {price} lei"
              values={{ price: formattedPrice }}
            />
          ) : (
            <span className={styles.priceSkeleton} aria-hidden />
          )}
        </div>
      </div>
    </Link>
  )
}
